<script setup lang="ts">
import { Head } from "@inertiajs/vue3"
import { SbDetailRow, SbList } from "@unio/saltbox-ui"
import { shallowRef } from "vue"

import NarrowPage from "@/components/NarrowPage.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"

interface CreatedAccessToken {
  id: string
  description: string
  permission: "read" | "write"
  token_prefix: string
  value: string
}

const props = defineProps<{
  token: CreatedAccessToken
}>()

const { title } = useNavbar({ title: "Token Created" })
const copied = shallowRef(false)

async function copyToken() {
  await navigator.clipboard.writeText(props.token.value)
  copied.value = true
  setTimeout(() => (copied.value = false), 2000)
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <UAlert
      :icon="ICON.error"
      color="warning"
      variant="subtle"
      title="Copy this token now"
      description="This is the only time Ninjanizr will display the complete token."
    />

    <div class="flex flex-col gap-2">
      <UFormField label="API token">
        <UInput
          :model-value="props.token.value"
          readonly
          autocomplete="off"
          class="w-full"
          :ui="{ base: 'font-mono text-xs select-all' }"
        />
      </UFormField>
      <UButton
        :icon="copied ? ICON.check : ICON.duplicate"
        :label="copied ? 'Copied' : 'Copy token'"
        color="neutral"
        variant="subtle"
        class="self-start"
        @click="copyToken"
      />
    </div>

    <SbList bordered separator>
      <SbDetailRow label="Description" :value="props.token.description" />
      <SbDetailRow label="Permission" :value="props.token.permission" />
    </SbList>

    <UButton
      to="/account/access_tokens"
      label="Done"
      color="neutral"
      variant="outline"
      class="self-start"
    />
  </NarrowPage>
</template>
